import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Sparkles, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

const Quiz = ({ lesson, onCompleted }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  const submit = async () => {
    const s = lesson.quiz.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);
    setScore(s);
    setSubmitted(true);
    try {
      await api.post(`/learn/lessons/${lesson.id}/complete`, { score: s });
      toast.success(`Lesson complete — quiz score ${s}/${lesson.quiz.length}`);
      onCompleted(s);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const retake = () => { setAnswers({}); setSubmitted(false); setScore(null); };

  return (
    <div className="widget p-6" data-testid="lesson-quiz">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-heading font-bold text-lg text-white tracking-tight">Quick quiz</h2>
        {submitted && (
          <span className={`font-mono text-sm ${score === lesson.quiz.length ? "text-gain" : "text-yellow-500"}`} data-testid="quiz-score">
            Score: {score}/{lesson.quiz.length}
          </span>
        )}
      </div>
      <div className="space-y-6">
        {lesson.quiz.map((q, qi) => (
          <div key={qi} data-testid={`quiz-question-${qi}`}>
            <p className="text-sm text-white font-medium mb-2">{qi + 1}. {q.q}</p>
            <div className="space-y-1.5">
              {q.options.map((opt, oi) => {
                const selected = answers[qi] === oi;
                let cls = selected ? "border-primary bg-primary/10 text-white" : "border-border text-gray-300 hover:bg-white/5";
                if (submitted) {
                  if (oi === q.answer) cls = "border-gain bg-gain/10 text-gain";
                  else if (selected) cls = "border-loss bg-loss/10 text-loss";
                  else cls = "border-border text-muted-foreground";
                }
                return (
                  <button key={oi} disabled={submitted} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                    data-testid={`quiz-q${qi}-option-${oi}`}
                    className={`w-full text-left px-3 py-2 text-sm border rounded-sm transition-colors duration-150 flex items-center gap-2 ${cls}`}>
                    {submitted && oi === q.answer && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}
                    {submitted && selected && oi !== q.answer && <XCircle className="w-3.5 h-3.5 shrink-0" />}
                    {opt}
                  </button>
                );
              })}
            </div>
            {submitted && <p className="text-xs text-muted-foreground mt-2 pl-1">{q.explain}</p>}
          </div>
        ))}
      </div>
      <div className="mt-5">
        {!submitted ? (
          <Button onClick={submit} disabled={Object.keys(answers).length < lesson.quiz.length}
            className="rounded-sm bg-primary hover:bg-primary/85 text-white" data-testid="quiz-submit-button">
            Submit answers
          </Button>
        ) : (
          <Button onClick={retake} variant="outline" className="rounded-sm border-border" data-testid="quiz-retake-button">
            Retake quiz
          </Button>
        )}
      </div>
    </div>
  );
};

const AskTutor = ({ lessonId }) => {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);

  const ask = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setAnswer(null);
    try {
      const { data } = await api.post(`/learn/lessons/${lessonId}/ask`, { question: question.trim() });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-6 bg-blue-950/20 border-primary/20" data-testid="ask-tutor-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="font-heading font-bold text-lg text-white tracking-tight">Explain this — ask the AI tutor</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Anything unclear in this lesson? Ask a follow-up question.</p>
      <div className="flex gap-2">
        <Input value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. Why does compounding accelerate over time?"
          className="rounded-sm bg-background" data-testid="tutor-question-input" />
        <Button onClick={ask} disabled={loading || !question.trim()}
          className="rounded-sm bg-primary hover:bg-primary/85 text-white shrink-0" data-testid="tutor-ask-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}
        </Button>
      </div>
      {answer && (
        <div className="mt-4 fade-up" data-testid="tutor-answer">
          {answer.split("\n").map((l, i) => (
            <p key={i} className="text-sm text-gray-100 leading-relaxed">{l || "\u00a0"}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Lesson() {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    setLesson(null);
    api.get(`/learn/lessons/${lessonId}`).then((r) => setLesson(r.data));
  }, [lessonId]);

  if (!lesson) return <Skeleton className="h-96 rounded-sm bg-card" />;

  return (
    <div className="space-y-6 fade-up max-w-3xl">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors duration-150"
        data-testid="back-to-learn-link">
        <ArrowLeft className="w-4 h-4" /> Learning Centre
      </Link>
      <div>
        <div className="overline-label mb-2 flex items-center gap-3">
          <span>{lesson.track} track</span>
          <span className="flex items-center gap-1 normal-case"><Clock className="w-3 h-3" /> {lesson.minutes} min read</span>
          {lesson.completed && <span className="text-gain flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> completed</span>}
        </div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">{lesson.title}</h1>
      </div>

      <div className="widget p-6 space-y-4" data-testid="lesson-content">
        {lesson.content.map((para, i) => (
          <p key={i} className="text-sm text-gray-200 leading-relaxed">{para}</p>
        ))}
      </div>

      <Quiz lesson={lesson} onCompleted={(s) => setLesson({ ...lesson, completed: true, score: s })} />
      <AskTutor lessonId={lesson.id} />

      <p className="text-xs text-muted-foreground border-t border-border pt-4">
        This is educational content, not financial advice.
      </p>
    </div>
  );
}
